import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import {
  EnumCompanySize,
  EnumProjectHourlyRate,
  EnumCurrency,
  EnumProjectSize,
  EnumService,
  mapCompanySize,
  mapCountry,
  mapHourlyRate,
  mapIndustry,
  mapLanguage,
  mapProjectSize,
  mapService,
  EnumIndustry,
  EnumLanguage,
  EnumCountry,
} from 'tdc-web-backend/enums/enums';
import { CompanyClientDto } from 'tdc-web-backend/company-client/schemas';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseInputLabel from '../../../components/custom-inputs/BaseInputLabel';
import useUserCompany from '../../../utils/hooks/useUserCompany';
import CustomInput from '../../../components/custom-input/CustomInput';
import CustomButton from '../../../components/button/CustomButton';
import useAuth from '../../../utils/hooks/useAuth';
import useGetMany from '../../../utils/hooks/crud-hooks/useGetMany';
import { ReactComponent as PlusNoBgIcon } from '../../../assets/icons/button-icons/PlusNoBgIcon.svg';
import CustomChip from '../../../components/custom-chip/CustomChip';
import AddClientModal from '../PostRegistration/components/AddClientModal';
import { getBase64 } from '../../../utils/helpers';
import ControlledDropzoneFileInput from '../../../components/custom-inputs/ControlledDropzoneFileInput/ControlledDropzoneFileInput';
import { primaryDark } from '../../../utils/color';
import { ReactComponent as FileUploadSvg } from '../../../assets/icons/project-icons/FileUploadSvg.svg';

export type TClient = {
  id: string;
  name: string;
  avatar: string;
};

const UnlockHiring = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    authData: { userData: user },
  } = useAuth();

  const { company, unlockHiring, isLoadingUnlockHiring: isSubmitting } = useUserCompany();

  const { data: clients } = useGetMany<CompanyClientDto>({
    resource: `company-client/?company=${user?.membership?.company}`,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: !!user?.membership?.company || false,
  });

  const formMethods = useForm({
    defaultValues: {
      services: [] as EnumService[],
      currency: EnumCurrency.Eur,
      averageProjectSize: '' as EnumProjectSize | '',
      companySize: '' as EnumCompanySize | '',
      hourlyRate: '' as EnumProjectHourlyRate | '',
      languages: [] as EnumLanguage[],
      targetCountries: [] as EnumCountry[],
      targetIndustries: [] as EnumIndustry[],
      clients: [] as TClient[],
      avatar: '' as string,
    },
  });
  const {
    watch,
    setValue,
    reset,
    trigger,
    control,
    formState,
    clearErrors,
    handleSubmit,
    getValues,
  } = formMethods;

  useEffect(() => {
    const resetForm = async () => {
      if (company && clients) {
        reset({
          services: company.services || [],
          currency: company.currency || EnumCurrency.Eur,
          averageProjectSize: company.averageProjectSize || '',
          companySize: company.companySize || '',
          hourlyRate: company.hourlyRate || '',
          languages: company.languages || [],
          targetCountries: company.targetCountries || [],
          targetIndustries: company.targetIndustries || [],
          clients: clients.data.results.map((client) => ({
            id: client.id,
            name: client.name,
            avatar: client.avatar || '',
          })) as unknown as TClient[],
          avatar: company.avatar || '',
        });
        trigger();
      }
    };
    resetForm();
  }, [company, clients]);

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'clients',
  });

  const onUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    clearErrors('avatar');

    const base64 = await getBase64(file);
    setValue('avatar', base64 as string);
  };

  const currency = watch('currency');

  const hasAvatarError = formState.errors ? Object.keys(formState.errors).includes('avatar') : null;

  const onSubmit = (data: any) => {
    const payload = { ...data, clients: data.clients.map(({ id }: { id: string }) => id) };

    if (payload.avatar && !payload.avatar.startsWith('data:')) {
      delete payload.avatar;
    }
    unlockHiring(payload, {
      onSuccess: () => {
        navigate('/dashboard');
      },
      onError: () => {
        console.log('Error updating company');
      },
    });
  };

  if (!company) return null; // or a loading spinner

  return (
    <FormProvider {...formMethods}>
      <Stack component="form" sx={{ p: 3, maxWidth: 1000 }} onSubmit={handleSubmit(onSubmit)}>
        <BaseInputLabel text={t('common.companyDetails')} />
        <Divider />
        <Stack direction="row" sx={{ margin: '24px 0 32px 0' }}>
          <Stack
            spacing={3}
            width="50%"
            pr={3}
            sx={{ borderRight: '1px solid', borderColor: 'secondaryBlue.100' }}
          >
            <Stack alignItems="center" justifyContent="center">
              <ControlledDropzoneFileInput
                name="avatar"
                onUpload={onUpload}
                required
                dropzoneProps={{
                  multiple: false,
                }}
                renderCustomDroppableAreaWidth={135}
                renderCustomDroppableAreaHeight={135}
                renderCustomDroppableArea={({ getRootProps, getInputProps }) => (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    {...getRootProps()}
                    spacing={1}
                    sx={{
                      width: 135,
                      height: 135,
                      borderRadius: '50%',
                      border: '2px dashed',
                      borderColor: hasAvatarError ? 'secondaryPink.500' : 'primaryDark.300',
                      '.MuiTypography-root': {
                        fontSize: '12px',
                      },
                      backgroundColor: hasAvatarError ? 'secondaryPink.100' : 'primaryDark.200',
                    }}
                  >
                    <input {...getInputProps()} />
                    <FileUploadSvg fill={primaryDark[400]} />
                    <Typography color="primaryDark.400" fontWeight="600">
                      ADD LOGO
                    </Typography>
                  </Stack>
                )}
              />

              {hasAvatarError && (
                <Typography
                  color="secondaryPink.500"
                  variant="body4"
                  sx={{ alignSelf: 'start', display: 'block', margin: '12px 0' }}
                >
                  This field is required
                </Typography>
              )}
            </Stack>

            <CustomInput
              input={<CustomInput.Autocomplete />}
              name="services"
              choices={mapService}
              translation={{ ns: 'enum' }}
              placeholder={t('common.select')}
              label={t('settings.companyProfile.offeredServices')}
              required
              multiple
              withChips
              disabled={isSubmitting}
            />

            <CustomInput
              input={<CustomInput.Select />}
              name="companySize"
              choices={mapCompanySize}
              placeholder={t('common.select')}
              label={t('settings.companyProfile.overviewDetails.companySize')}
              required
              disabled={isSubmitting}
            />
            <Stack>
              <BaseInputLabel text={t('settings.companyProfile.selectOperatingCurrency')} />

              <Stack direction="row">
                <CustomButton
                  variant="secondary"
                  disabled={isSubmitting}
                  sx={{
                    borderTopLeftRadius: '0px !important',
                    borderBottomLeftRadius: '0px !important',
                    borderColor: 'secondaryBlue.100',
                    fontSize: '12px',
                    color: currency === EnumCurrency.Eur ? 'primaryLight.500' : 'primaryDark.500',
                    boxShadow:
                      currency === EnumCurrency.Eur ? '6px 6px 15px 0px #3845721A' : 'initial',
                  }}
                  onClick={() => setValue('currency', EnumCurrency.Eur)}
                >
                  EUR
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  disabled={isSubmitting}
                  sx={{
                    borderTopRightRadius: '0px !important',
                    borderBottomRightRadius: '0px !important',
                    borderColor: 'secondaryBlue.100',
                    fontSize: '12px',
                    color: currency === EnumCurrency.Usd ? 'primaryLight.500' : 'primaryDark.500',
                    boxShadow:
                      currency === EnumCurrency.Usd ? '6px 6px 15px 0px #3845721A' : 'initial',
                  }}
                  onClick={() => setValue('currency', EnumCurrency.Usd)}
                >
                  USD
                </CustomButton>
              </Stack>
            </Stack>

            <Stack>
              <BaseInputLabel text={t('settings.companyProfile.overviewDetails.avgProjectSize')} />

              <Stack direction="row" alignItems="baseline">
                <Stack
                  sx={{
                    width: 60,
                    height: 47,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'primaryDark.250',
                    borderRightColor: 'transparent',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  <Typography sx={{ px: 2, fontSize: '14px' }}>
                    {watch('currency')?.toUpperCase()}
                  </Typography>
                </Stack>

                <CustomInput
                  input={<CustomInput.Select />}
                  name="averageProjectSize"
                  placeholder={t('common.select')}
                  choices={mapProjectSize}
                  disabled={isSubmitting}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      borderTopLeftRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    },
                  }}
                  required
                />
              </Stack>
            </Stack>

            <Stack>
              <BaseInputLabel text={t('settings.companyProfile.overviewDetails.hourlyRate')} />

              <Stack direction="row" maxHeight="fit-content" alignItems="baseline">
                <Stack
                  sx={{
                    width: 60,
                    height: 47,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'primaryDark.250',
                    borderRightColor: 'transparent',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  <Typography sx={{ px: 2, fontSize: '14px' }}>
                    {watch('currency')?.toUpperCase()}
                  </Typography>
                </Stack>

                <CustomInput
                  input={<CustomInput.Select />}
                  name="hourlyRate"
                  placeholder={t('common.select')}
                  choices={mapHourlyRate}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      borderTopLeftRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    },
                  }}
                  required
                  disabled={isSubmitting}
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack spacing={3} width="50%" pl={3}>
            <CustomInput
              input={<CustomInput.Autocomplete />}
              name="languages"
              label={t('settings.companyProfile.workingLanguages')}
              placeholder={t('common.select')}
              choices={mapLanguage}
              translation={{ ns: 'enum' }}
              multiple
              withChips
              required
              disabled={isSubmitting}
            />
            <CustomInput
              input={<CustomInput.Autocomplete />}
              name="targetCountries"
              label={t('settings.companyProfile.targetMarkets')}
              placeholder={t('common.select')}
              choices={mapCountry}
              translation={{ t, ns: 'enum' }}
              required
              withChips
              multiple
              disabled={isSubmitting}
            />

            <CustomInput
              input={<CustomInput.Autocomplete />}
              name="targetIndustries"
              label={t('settings.companyProfile.targetIndustries')}
              placeholder={t('common.select')}
              choices={mapIndustry}
              translation={{ ns: 'enum' }}
              required
              multiple
              withChips
              disabled={isSubmitting}
            />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <BaseInputLabel text={t('settings.companyProfile.clients')} />

                <CustomButton
                  onClick={() => setIsOpen(true)}
                  variant="primaryText"
                  endIcon={<PlusNoBgIcon />}
                  disabled={isSubmitting}
                >
                  {t('common.add')}
                </CustomButton>
              </Stack>
            </Stack>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(fields as any[])?.map((field, index) => (
                <CustomChip
                  sx={{ maxWidth: 'fit-content' }}
                  key={`${field.name}-${index}`}
                  label={field.name as string}
                  onDelete={() => remove(index)}
                  icon={
                    <Avatar
                      sx={{ width: '20px', height: '20px' }}
                      src={field.avatar}
                      alt="Company logo"
                    />
                  }
                />
              ))}
            </Box>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" spacing={2} justifyContent="end" sx={{ mt: 3 }}>
          <CustomButton
            onClick={() => navigate('/dashboard')}
            variant="primaryLight"
            loading={isSubmitting}
          >
            {t('common.skipForNow')}
          </CustomButton>
          <CustomButton type="submit" variant="primary" loading={isSubmitting}>
            {t('common.completeSetups')}
          </CustomButton>
        </Stack>
      </Stack>
      {isOpen && <AddClientModal isOpen={isOpen} setIsOpen={setIsOpen} append={append} />}
    </FormProvider>
  );
};

export default UnlockHiring;
